import type {
  ClipboardDocument,
  ClipboardFolder,
} from "@/features/clipboard/api/clipboard";
import { clipboardDocumentFileType } from "@/features/clipboard/utils/documentFileType";

export type SortColumn = "relevance" | "name" | "type" | "updated";
export type SortDirection = "asc" | "desc";

/** Folders and files share one listing, sorted together (no folders-first). */
export type ListRow =
  | { kind: "folder"; folder: ClipboardFolder }
  | { kind: "doc"; doc: ClipboardDocument };

export const sortMergedRows = (
  folders: ClipboardFolder[],
  documents: ClipboardDocument[],
  sortColumn: SortColumn,
  sortDirection: SortDirection,
): ListRow[] => {
  const merged: ListRow[] = [
    ...folders.map((folder) => ({ kind: "folder" as const, folder })),
    ...documents.map((doc) => ({ kind: "doc" as const, doc })),
  ];
  const name = (r: ListRow) =>
    r.kind === "folder" ? r.folder.name : r.doc.filename || r.doc.title;
  // Missing timestamps (e.g. empty or shared folders) sink to the bottom in
  // both time orderings rather than floating to the top.
  const when = (r: ListRow) =>
    r.kind === "folder"
      ? r.folder.latest_at || ""
      : r.doc.updated_at || r.doc.created_at || "";
  const fileType = (row: ListRow) =>
    row.kind === "folder" ? "Folder" : clipboardDocumentFileType(row.doc);
  if (sortColumn === "relevance") {
    return merged.sort((a, b) => {
      const aRank = a.kind === "folder" ? a.folder.search_rank : a.doc.search_rank;
      const bRank = b.kind === "folder" ? b.folder.search_rank : b.doc.search_rank;
      return (aRank ?? Number.MAX_SAFE_INTEGER) - (bRank ?? Number.MAX_SAFE_INTEGER);
    });
  }
  merged.sort((a, b) => {
    const wa = when(a);
    const wb = when(b);
    let comparison = 0;
    if (sortColumn === "name") {
      comparison = name(a).localeCompare(name(b));
    } else if (sortColumn === "type") {
      comparison =
        fileType(a).localeCompare(fileType(b)) || name(a).localeCompare(name(b));
    } else {
      // Missing timestamps always sink, regardless of direction.
      if (!wa || !wb) {
        if (wa !== wb) return wa ? -1 : 1;
        comparison = name(a).localeCompare(name(b));
      } else {
        comparison = wa.localeCompare(wb) || name(a).localeCompare(name(b));
      }
    }
    return sortDirection === "asc" ? comparison : -comparison;
  });
  return merged;
};
